// tabs/LocationsTab.tsx
import { useCallback, useEffect, useState } from 'react';
import { Select, Button, Group, Divider, SimpleGrid, Card, Text, ActionIcon, Flex } from '@mantine/core';
import { IconPlus, IconTrash, IconRefresh } from '@tabler/icons-react';
import Tooltip from '@/components/ui/overlays/Tooltip';
import { useLocationService, useACCounsellingServices, LeaderLocationView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import ConfirmActionModal from './ConfirmActionModal';

interface LocationsTabProps {
  leaderId: string;
  active: boolean;
  onLeaderChange?: () => void;
}

export default function LocationsTab({ leaderId, active, onLeaderChange }: LocationsTabProps) {
  const Locations = useLocationService();
  const ACCounsellingServices = useACCounsellingServices();

  const [locations, setLocations] = useState<LeaderLocationView[]>([]);
  const [allLocations, setAllLocations] = useState<any[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('Confirm Remove');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => Promise<void>) | null>(null);

  const fetchLeaderLocations = useCallback(async () => {
    if (!leaderId) return;
    setLoading(true);
    try {
      const result = await ACCounsellingServices.leader.getLocations(leaderId);
      setLocations(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [leaderId, ACCounsellingServices]);

  useEffect(() => {
    Locations.getAll().then(setAllLocations).catch(console.error);
  }, [Locations]);

  useEffect(() => { if (active) fetchLeaderLocations(); }, [active, fetchLeaderLocations]);

  const getLocationName = (locationID: number) =>
    allLocations.find((l) => l.locationID === locationID)?.name ?? `Location ${locationID}`;

  const getLeaderLocationRowId = (l: any) => l.leaderLocationID ?? l.leaderLocationId ?? l.id ?? l.locationID;

  const handleAddLocation = async () => {
    if (!selectedLocationId || !leaderId) return;
    setLoading(true);
    try {
      await ACCounsellingServices.leader.addLocation(leaderId, { locationID: Number(selectedLocationId) } as any);
      setSelectedLocationId(null);
      await fetchLeaderLocations();
      onLeaderChange?.();
      toast.success('Location Added', 'Location assigned to leader.');
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to add location.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLocation = (leaderLocationRowId: number, locationName?: string) => {
    setConfirmTitle('Remove Location');
    setConfirmMessage(`Remove this location${locationName ? ` (${locationName})` : ''} from the leader?`);
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        await ACCounsellingServices.leader.removeLocation(leaderId, leaderLocationRowId as any);
        await fetchLeaderLocations();
        onLeaderChange?.();
        toast.warning('Location Removed', 'Location removed from leader.');
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove location.');
      } finally {
        setLoading(false);
      }
    });
    setConfirmOpen(true);
  };

  return (
    <>
      <ConfirmActionModal
        opened={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      <Group gap="sm" justify="space-between" mb="md">
        <Select
          placeholder="Select a location to add"
          style={{ width: 300 }}
          data={allLocations
            .filter((l) => !(locations.map((ll) => ll.locationID) ?? []).includes(l.locationID))
            .map((l) => ({ value: String(l.locationID), label: l.name ?? l.locationName ?? `Location ${l.locationID}` }))}
          value={selectedLocationId}
          onChange={setSelectedLocationId}
          searchable
        />
        
        <Group gap="sm">
        <Flex justify="right" align="center">
        <Tooltip label="Refresh">
          <ActionIcon variant="subtle" onClick={fetchLeaderLocations} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Flex>
        <Button leftSection={<IconPlus size={16} />} onClick={handleAddLocation} disabled={!selectedLocationId} loading={loading}>
          Add Location
        </Button>
        </Group>
      </Group>
      <Divider mb="md" />
      {locations.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No locations assigned.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          {locations.map((l: any) => (
            <Card key={getLeaderLocationRowId(l)} withBorder radius="md" padding="lg">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Flex direction="column" gap={4}>
                  <Text fw={700}>{l.locationName ?? getLocationName(l.locationID)}</Text>
                  <Text size="sm" c="dimmed">{l.caseCount} cases</Text>
                </Flex>
                <ActionIcon
                  variant="light"
                  color="red"
                  onClick={() => handleRemoveLocation(getLeaderLocationRowId(l), l.locationName ?? getLocationName(l.locationID))}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </>
  );
}